<script lang="ts" setup>
import axios from 'axios'
import { computed, inject, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router'

import { ResourceResponse, uuid } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { imageViewerOpenKey } from '@/types/global-injection-keys'
import { PackagingOrder, PackagingOrderStatus, PackagingOrderType } from '@/types/packaging'
import { image as imageHelper, localAsset } from '@/utils/assets'
import { datetime } from '@/utils/dates'
import { emitter } from '@/utils/mitt'
import { packagingOrderStatus, packagingOrderTypes } from '@/utils/packaging-order'

import ManagePackagingOrderModal from '@/components/packaging/ManagePackagingOrderModal.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import UpdatePackagingOrderStatusModal from '@/components/packaging/UpdatePackagingOrderStatusModal.vue'
import EntityLogs from '@/components/EntityLogs.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import ContentHeading from '@/components/layout/ContentHeading.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyModal from '@/components/my-components/MyModal.vue'

export interface Props {
    id?: string
    open?: boolean
}

const props = withDefaults(defineProps<Props>(), { open: true })
const emit = defineEmits<{ (e: 'update:open', value: boolean): void }>()

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

const imageViewerOpen = inject(imageViewerOpenKey)!

const packagingOrder = ref<PackagingOrder | null>(null)
const loading = ref(false)
const updateStatusModalOpen = ref(false)
const managePackagingOrderModalOpen = ref(false)
const selectedStatus = ref<PackagingOrderStatus>(PackagingOrderStatus.Accepted)

const canCancelOrder = computed(() => {
    if (!packagingOrder.value) return false
    if (packagingOrder.value.status === PackagingOrderStatus.Finished) return false
    if (packagingOrder.value.status === PackagingOrderStatus.Canceled) return false

    return true
})

const canAcceptOrder = computed(() => {
    if (!packagingOrder.value) return false
    if (packagingOrder.value.status === PackagingOrderStatus.Finished) return false
    if (packagingOrder.value.status === PackagingOrderStatus.Accepted) return false
    if (packagingOrder.value.companyId === authStore.companyId) return false

    return true
})

const canFinishOrder = computed(() => {
    if (!packagingOrder.value) return false
    if (packagingOrder.value.status !== PackagingOrderStatus.Accepted) return false
    if (packagingOrder.value.supplierCompany.id !== authStore.companyId) return false

    return true
})

const finishButtonText = computed(() => {
    if (!packagingOrder.value) return ''
    if (packagingOrder.value.type === PackagingOrderType.Delivery) {
        return t('markAsDelivered')
    } else if (packagingOrder.value.type === PackagingOrderType.Pickup) {
        return t('markAsPickedUp')
    }
    return t('finish')
})

function transactionLink(id: string): RouteLocationRaw {
    return {
        name: 'packaging.transactions.show',
        params: { transactionId: id },
    }
}

async function fetchPackagingOrder(id?: uuid) {
    loading.value = true
    try {
        const response = await axios.get<ResourceResponse<PackagingOrder>>(
            window.route('packaging.company.packaging-orders.show', {
                company: authStore.companyId,
                packaging_order: id ?? props.id!,
            }),
        )
        packagingOrder.value = response.data.data
    } finally {
        loading.value = false
    }
}

function changeStatus(status: PackagingOrderStatus) {
    updateStatusModalOpen.value = true
    selectedStatus.value = status
}

function onClose() {
    if (imageViewerOpen.value) return

    if (props.id) {
        emit('update:open', false)
    } else {
        router.replace({ name: 'packaging.packaging-orders', query: route.query })
    }
}

function refetchPackagingOrder() {
    emitter.emit('fetchPackagingOrders')
    if (packagingOrder.value) fetchPackagingOrder(packagingOrder.value.id)
}

onBeforeMount(async () => {
    if (route.params.packagingOrderId) fetchPackagingOrder(route.params.packagingOrderId as uuid)
    if (props.id) await fetchPackagingOrder()
})

watch(
    () => props.id,
    () => {
        if (props.id) {
            fetchPackagingOrder()
        }
    },
)
watch(
    () => route.params.packagingOrderId,
    () => {
        if (route.params.packagingOrderId)
            fetchPackagingOrder(route.params.packagingOrderId as uuid)
    },
)
</script>

<template>
    <MyModal :value="props.open" :max-width="700" @close="onClose">
        <LoaderWrapper :visible="loading" />
        <div v-if="packagingOrder" class="flex flex-col justify-between space-y-5">
            <ContentHeading class="flex justify-between items-center">
                <div class="flex flex-col space-x-1">
                    <h1 class="text-xl font-semibold text-primary-400">
                        {{ t('packagingOrder') }} -
                        {{ t(packagingOrderTypes[packagingOrder.type]) }}
                    </h1>
                    <div class="flex space-x-2 text-xs">
                        <h4 class="font-semibold text-primary-500 dark:text-primary-300">
                            {{ t('orderNumber') }}:
                        </h4>
                        <h4
                            class="font-medium dark:text-primary-400"
                            v-text="packagingOrder.orderNumber"
                        />
                    </div>
                </div>

                <div class="flex space-x-3">
                    <MyButton
                        v-if="packagingOrder.status === PackagingOrderStatus.Created"
                        scheme="primary"
                        size="small"
                        @click="managePackagingOrderModalOpen = true"
                    >
                        {{ t('edit') }}
                    </MyButton>
                    <MyButton
                        v-if="canCancelOrder"
                        scheme="danger"
                        size="small"
                        @click="changeStatus(PackagingOrderStatus.Canceled)"
                        v-text="t('cancel')"
                    />
                    <MyButton
                        v-if="canAcceptOrder"
                        scheme="primary"
                        size="small"
                        @click="changeStatus(PackagingOrderStatus.Accepted)"
                        v-text="t('accept')"
                    />
                    <MyButton
                        v-if="canFinishOrder"
                        scheme="primary"
                        size="small"
                        @click="changeStatus(PackagingOrderStatus.Finished)"
                        v-text="finishButtonText"
                    />
                </div>
            </ContentHeading>

            <div class="flex flex-col bg-white dark:bg-dark-500 rounded-xl p-6 space-y-4">
                <div class="flex justify-between space-x-2">
                    <div class="flex w-1/2 flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('customer')"
                        />
                        <span
                            class="font-semibold text-primary-500 dark:text-primary-100"
                            v-text="packagingOrder.customerCompany.name"
                        />
                        <span
                            class="text-sm text-primary-500 dark:text-primary-100"
                            v-text="packagingOrder.customerLocation?.name"
                        />
                    </div>
                    <div class="flex w-1/2">
                        <div class="flex flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('supplier')"
                            />
                            <span
                                class="font-semibold text-primary-500 dark:text-primary-100"
                                v-text="packagingOrder.supplierCompany.name"
                            />
                            <span
                                v-if="packagingOrder.supplierLocation"
                                class="text-sm text-primary-500 dark:text-primary-100"
                                v-text="packagingOrder.supplierLocation?.name"
                            />
                            <span
                                v-else
                                class="text-sm text-red-500"
                                v-text="t('noSupplierLocation')"
                            />
                        </div>
                    </div>
                </div>

                <div class="flex justify-between space-x-2">
                    <div class="flex w-1/2 flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('createdAt')"
                        />
                        <span
                            class="font-semibold text-primary-500 dark:text-primary-100"
                            v-text="datetime(packagingOrder.createdAt)"
                        />
                    </div>
                    <div class="flex space-x-10 w-1/2">
                        <div class="flex flex-col">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('status')"
                            />
                            <span
                                class="font-semibold text-primary-500 dark:text-primary-100"
                                v-text="t(packagingOrderStatus[packagingOrder.status])"
                            />
                        </div>
                        <div v-if="packagingOrder.transactionId" class="flex flex-col print:hidden">
                            <span
                                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                                v-text="t('transaction')"
                            />

                            <RouterLink
                                :to="transactionLink(packagingOrder.transactionId)"
                                class="flex space-x-1.5 justify-center items-center hover:opacity-70"
                            >
                                <span
                                    class="font-semibold text-primary-500 dark:text-primary-100"
                                    v-text="t('view')"
                                />
                                <mdi:package-check />
                            </RouterLink>
                        </div>
                    </div>
                </div>
                <div class="flex w-1/2 flex-col">
                    <span
                        class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                        v-text="
                            t(
                                packagingOrder.type === PackagingOrderType.Delivery
                                    ? 'deliveryDate'
                                    : 'pickupDate',
                            )
                        "
                    />
                    <span
                        class="font-semibold text-primary-500 dark:text-primary-100"
                        v-text="datetime(packagingOrder.plannedAt)"
                    />
                </div>
            </div>

            <!-- Products -->
            <MyPanel
                v-if="packagingOrder.products.length !== 0"
                bordered
                panel-class="overflow-hidden"
            >
                <table class="w-full">
                    <thead>
                        <tr
                            class="border-b border-primary-200 bg-primary-100 text-xs dark:border-dark-400 dark:bg-dark-600"
                        >
                            <th class="p-2.5 font-semibold" v-text="t('name')" />
                            <th
                                class="w-20 border-l border-primary-200 p-2.5 font-semibold dark:border-dark-400"
                                v-text="t('amount')"
                            />
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="product in packagingOrder.products"
                            :key="product.id"
                            class="border-t border-primary-200 first:border-t-0 even:bg-primary-100 dark:border-dark-400 dark:even:bg-dark-600"
                        >
                            <td>
                                <div class="flex space-x-5 items-center">
                                    <img
                                        class="p-2 w-16"
                                        :src="imageHelper(product.image, 'thumbnail')"
                                        :alt="product.name"
                                    />
                                    <span v-text="product.name" />
                                </div>
                            </td>
                            <td
                                v-if="product.delivered > 0"
                                class="border-l border-primary-200 text-center dark:border-dark-400"
                                v-text="product.delivered"
                            />
                            <td
                                v-else-if="product.received > 0"
                                class="border-l border-primary-200 text-center dark:border-dark-400"
                                v-text="product.received"
                            />
                        </tr>
                    </tbody>
                </table>
            </MyPanel>
            <div class="flex flex-col">
                <h1
                    class="text-xl font-semibold text-primary-400 uppercase"
                    v-text="t('activity')"
                />
                <EntityLogs
                    :model-id="packagingOrder.id"
                    :logs="packagingOrder.logs"
                    :entity-name="t('packagingOrderCreated')"
                    entity-type="packagingOrder"
                    @log-added="
                        fetchPackagingOrder(route.params.packagingOrderId as uuid | undefined)
                    "
                />
            </div>
        </div>

        <img
            :src="localAsset('assets/begreenprintlabel.png')"
            alt="begreen"
            class="absolute bottom-0 right-0 w-32 h-32 hidden opacity-30 print:block"
        />

        <h1
            v-if="!packagingOrder && !loading"
            class="my-auto text-center text-4xl font-bold"
            v-text="t('packagingOrderNotFound')"
        />
    </MyModal>

    <UpdatePackagingOrderStatusModal
        v-if="packagingOrder && updateStatusModalOpen"
        v-model="updateStatusModalOpen"
        :packaging-order="packagingOrder"
        :status="selectedStatus"
        @saved="refetchPackagingOrder"
        @close="updateStatusModalOpen = false"
    />

    <ManagePackagingOrderModal
        v-if="managePackagingOrderModalOpen"
        v-model="managePackagingOrderModalOpen"
        :packaging-order-id="packagingOrder?.id"
        @close="managePackagingOrderModalOpen = false"
        @saved="fetchPackagingOrder(packagingOrder?.id)"
    />
</template>
